
import { Investor } from "@/types/investor";
import { getAPIKey, getAllStoredApiKeys } from "./apiKeyUtils";
import { enrichmentDebugState } from "./debugUtils";
import { enrichWithPerplexity } from "./providers/perplexityUtils";
import { enrichWithOpenAI } from "./providers/openaiUtils";
import { toast } from "sonner";

/**
 * Enhanced function to enrich investor data - uses perplexity first if available
 */
export async function enrichInvestorData(
  investor: Investor,
  relevanceContext: string,
  apiKey: string
): Promise<Investor> {
  try {
    console.log(`Starting enrichment process for investor ${investor.id}`);
    
    // Get the Perplexity API key with explicit logging
    const perplexityKey = getAPIKey('perplexity', '');
    console.log(`Perplexity API key available: ${!!perplexityKey}`);
    
    // Get the OpenAI API key with explicit logging, fallback to provided apiKey
    const openaiKey = getAPIKey('openai', apiKey);
    console.log(`OpenAI API key available: ${!!openaiKey}`);
    
    // Log all API key locations for debugging
    getAllStoredApiKeys();
    
    if (perplexityKey) {
      try {
        // Use Perplexity as the primary enrichment source
        console.log("Using Perplexity for investor enrichment");
        const enrichedInvestor = await enrichWithPerplexity(investor, relevanceContext, perplexityKey);
        
        // Add explicit enrichment source flag
        enrichedInvestor.enrichedWith = "perplexity";
        
        // Add last update timestamp
        enrichedInvestor.lastRelevanceUpdate = new Date().toISOString();
        
        // Track timing and source for debugging
        enrichmentDebugState.provider = "perplexity";
        enrichmentDebugState.completedAt = new Date().toISOString();
        
        console.log(`Enriched investor ${investor.id} with Perplexity successfully`);
        return enrichedInvestor;
      } catch (perplexityError) {
        console.error("Perplexity enrichment failed:", perplexityError);
        
        // Check if we're in strict mode (re-enrich action)
        const strictMode = localStorage.getItem('enrich_strict_mode') === 'true';
        
        if (strictMode) {
          // In strict mode, don't fallback - surface the error
          console.log("Strict mode enabled - not falling back to OpenAI");
          enrichmentDebugState.failedProvider = "perplexity";
          enrichmentDebugState.failureReason = perplexityError.message || "Perplexity API error";
          
          // Mark investor as failed and return
          const failedInvestor = { ...investor };
          failedInvestor.enrichmentFailed = true;
          failedInvestor.enrichedWith = "perplexity-failed";
          
          throw perplexityError;
        }
        
        // Show a toast notification for debugging purposes if in development
        if (process.env.NODE_ENV === 'development') {
          toast.error("Perplexity enrichment failed, falling back to OpenAI", {
            description: perplexityError.message,
            duration: 3000,
          });
        }
        
        // Log failure for debugging
        enrichmentDebugState.failedProvider = "perplexity";
        enrichmentDebugState.failureReason = perplexityError.message || "Perplexity API error";
        
        // If Perplexity fails, fall back to OpenAI
        if (!openaiKey) {
          throw new Error("No OpenAI API key available for fallback");
        }
        
        console.log("Falling back to OpenAI for investor enrichment");
        const enrichedInvestor = await enrichWithOpenAI(investor, relevanceContext, openaiKey);
        
        // Add explicit enrichment source flag
        enrichedInvestor.enrichedWith = "openai-fallback";
        
        // Add last update timestamp
        enrichedInvestor.lastRelevanceUpdate = new Date().toISOString();
        
        // Track timing and source for debugging
        enrichmentDebugState.provider = "openai-fallback";
        enrichmentDebugState.completedAt = new Date().toISOString();
        
        console.log(`Enriched investor ${investor.id} with OpenAI (fallback) successfully`);
        return enrichedInvestor;
      }
    } else {
      // Fall back to OpenAI if no Perplexity key is available
      console.log("No Perplexity API key found, using OpenAI for investor enrichment");
      if (!openaiKey) {
        throw new Error("No API keys available for enrichment. Please add an OpenAI or Perplexity API key in the API Connections section.");
      }
      
      const enrichedInvestor = await enrichWithOpenAI(investor, relevanceContext, openaiKey);
      
      // Add explicit enrichment source flag
      enrichedInvestor.enrichedWith = "openai";
      
      // Add last update timestamp
      enrichedInvestor.lastRelevanceUpdate = new Date().toISOString();
      
      // Track timing and source for debugging
      enrichmentDebugState.provider = "openai";
      enrichmentDebugState.completedAt = new Date().toISOString();
      
      console.log(`Enriched investor ${investor.id} with OpenAI successfully`);
      return enrichedInvestor;
    }
  } catch (error) {
    console.error("Error enriching investor data:", error);
    // Set error in debug state
    enrichmentDebugState.error = error instanceof Error ? error.message : "Unknown enrichment error";
    enrichmentDebugState.completedAt = new Date().toISOString();
    
    // Display user-friendly error
    toast.error("Failed to enrich investor data. Please check your API keys in the API Connections section.");
    
    // Return original investor if there was an error, but mark it as failed
    const failedInvestor = { ...investor };
    failedInvestor.enrichmentFailed = true;
    
    return failedInvestor;
  }
}
